import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert } from '../ui';
import { Plus, Trash2, Save, Check, X, ArrowUpDown } from 'lucide-react';
import { api } from '../../services/api';

interface ToolRelation {
  relation_id: string;
  tool_id: string;
  tool_name: string;
  tool_description?: string;
  is_required: number;
  execution_order?: number;
  server_id: string;
  server_name?: string;
}

interface AvailableTool {
  id: string;
  item_name: string;
  item_description?: string;
  server_id: string;
  server_name?: string;
}

interface PromptToolRelationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  promptId: string;
  promptName: string;
}

export const PromptToolRelationsModal: React.FC<PromptToolRelationsModalProps> = ({
  isOpen,
  onClose,
  promptId,
  promptName
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [relations, setRelations] = useState<ToolRelation[]>([]);
  const [availableTools, setAvailableTools] = useState<AvailableTool[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedToolId, setSelectedToolId] = useState('');
  const [newIsRequired, setNewIsRequired] = useState(false);
  const [newExecutionOrder, setNewExecutionOrder] = useState<number | ''>('');
  const [editingRelation, setEditingRelation] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ is_required: boolean; execution_order: number | '' }>({
    is_required: false,
    execution_order: ''
  });

  useEffect(() => {
    if (isOpen && promptId) {
      fetchRelations();
      fetchAvailableTools();
    }
  }, [isOpen, promptId]);

  const fetchRelations = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.prompts.tools.list(promptId);
      setRelations(response.tools);
    } catch (err: any) {
      setError(err.message || 'Failed to load tool relations');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTools = async () => {
    try {
      // Get all tools from the new /api/tools endpoint
      const response = await api.tools.list();
      const tools = response.tools || [];

      setAvailableTools(tools.map((tool: any) => ({
        id: tool.id,
        item_name: tool.item_name,
        item_description: tool.item_description,
        server_id: tool.server_id,
        server_name: tool.server_name || 'Unknown Server'
      })));
    } catch (err) {
      console.error('Failed to load available tools:', err);
      setError('Failed to load available tools. Please try again.');
    }
  };

  const handleAddRelation = async () => {
    if (!selectedToolId) {
      setError('Please select a tool');
      return;
    }

    try {
      setError('');
      await api.prompts.tools.add(promptId, {
        tool_id: selectedToolId,
        is_required: newIsRequired,
        execution_order: newExecutionOrder === '' ? undefined : Number(newExecutionOrder)
      });

      setShowAddForm(false);
      setSelectedToolId('');
      setNewIsRequired(false);
      setNewExecutionOrder('');
      await fetchRelations();
    } catch (err: any) {
      setError(err.message || 'Failed to add tool relation');
    }
  };

  const handleStartEdit = (relation: ToolRelation) => {
    setEditingRelation(relation.relation_id);
    setEditValues({
      is_required: relation.is_required === 1,
      execution_order: relation.execution_order || ''
    });
  };

  const handleSaveEdit = async (relationId: string) => {
    try {
      setError('');
      await api.prompts.tools.update(promptId, relationId, {
        is_required: editValues.is_required,
        execution_order: editValues.execution_order === '' ? undefined : Number(editValues.execution_order)
      });

      setEditingRelation(null);
      await fetchRelations();
    } catch (err: any) {
      setError(err.message || 'Failed to update relation');
    }
  };

  const handleCancelEdit = () => {
    setEditingRelation(null);
  };

  const handleDeleteRelation = async (relationId: string) => {
    if (!window.confirm('Are you sure you want to remove this tool relation?')) {
      return;
    }

    try {
      setError('');
      await api.prompts.tools.remove(promptId, relationId);
      await fetchRelations();
    } catch (err: any) {
      setError(err.message || 'Failed to delete relation');
    }
  };

  const getAvailableToolsForAdd = () => {
    const usedToolIds = relations.map(r => r.tool_id);
    return availableTools.filter(tool => !usedToolIds.includes(tool.id));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Manage Tool Relations - ${promptName}`}
      size="lg"
    >
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Define which tools this prompt requires and their execution order
          </p>
          {!showAddForm && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAddForm(true)}
            >
              <Plus size={16} className="mr-1" />
              Add Tool
            </Button>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Add New Relation Form */}
        {showAddForm && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-gray-900">Add Tool Relation</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Tool
              </label>
              <select
                value={selectedToolId}
                onChange={(e) => setSelectedToolId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Select a tool --</option>
                {getAvailableToolsForAdd().map((tool) => (
                  <option key={tool.id} value={tool.id}>
                    {tool.item_name} [{tool.server_name}]{tool.item_description && ` - ${tool.item_description}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newIsRequired}
                    onChange={(e) => setNewIsRequired(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Required Tool</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Execution Order
                </label>
                <input
                  type="number"
                  value={newExecutionOrder}
                  onChange={(e) => setNewExecutionOrder(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddRelation}
                disabled={!selectedToolId}
              >
                Add Relation
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddForm(false);
                  setSelectedToolId('');
                  setNewIsRequired(false);
                  setNewExecutionOrder('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
            <span className="ml-3 text-gray-600">Loading tool relations...</span>
          </div>
        )}

        {/* Relations List */}
        {!loading && relations.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No tool relations defined for this prompt.
            {!showAddForm && ' Click "Add Tool" to create one.'}
          </div>
        )}

        {!loading && relations.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <ArrowUpDown size={18} className="text-blue-600" />
              Tool Relations ({relations.length})
            </h4>

            <div className="space-y-2">
              {relations.map((relation) => (
                <div
                  key={relation.relation_id}
                  className="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                >
                  {editingRelation === relation.relation_id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div className="font-medium text-gray-900">{relation.tool_name}</div>

                      <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={editValues.is_required}
                            onChange={(e) => setEditValues({ ...editValues, is_required: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Required</span>
                        </label>

                        <div>
                          <input
                            type="number"
                            value={editValues.execution_order}
                            onChange={(e) => setEditValues({ ...editValues, execution_order: e.target.value === '' ? '' : Number(e.target.value) })}
                            placeholder="Execution order"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(relation.relation_id)}
                          className="flex items-center gap-1 px-2 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                        >
                          <Check size={14} />
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                        >
                          <X size={14} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900">{relation.tool_name}</span>
                          {relation.server_name && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                              {relation.server_name}
                            </span>
                          )}
                          {relation.is_required === 1 && (
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                              Required
                            </span>
                          )}
                          {relation.execution_order && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                              Order: {relation.execution_order}
                            </span>
                          )}
                        </div>
                        {relation.tool_description && (
                          <p className="text-sm text-gray-600 mt-1">{relation.tool_description}</p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStartEdit(relation)}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit relation"
                        >
                          <Save size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteRelation(relation.relation_id)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Remove relation"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end pt-4 border-t border-gray-200">
          <Button
            variant="ghost"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};
